import React, { useMemo, useState } from "react";
import { Button, Card, Form, Row, Col } from "react-bootstrap";
import Spinner from "components/Spinner";
import { formatPhoneNumber } from "services/UtilityService";
import { useConfigForm } from "../configTool/useConfigForm";
import { validateCustomerCreateForm } from "../configTool/configFormValidators";
import { ConfigFormSwitch } from "../configTool/ConfigFormComponents";
import "./CreateCustomerForm.css";

const EMPTY_CUSTOMER_FORM = {
  customerName: "",
  programName: "",
  customerNameAbbreviation: "",
  logoUrl: "",
  supportNumber: "",
  faxNumberInbound: "",
  email: "",
  isAutoAcceptEnabled: false,
  isIntroFaxEnabled: false,
  showSavingPrices: true,
  botCallingAllowed: false,
  botEntityName: "",
};

const CreateCustomerForm = ({ onSubmit, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  const initialValues = useMemo(() => ({ ...EMPTY_CUSTOMER_FORM }), []);

  const {
    formState,
    errors,
    handleInputChange,
    handleCheckboxChange,
    runValidation,
  } = useConfigForm({
    initialValues,
    validate: validateCustomerCreateForm,
  });

  const handleSubmit = () => {
    setShowValidation(true);
    if (!runValidation()) {
      return;
    }
    setLoading(true);
    onSubmit(formState, () => setLoading(false));
  };

  if (loading || !formState) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <Form>
        <Card className="mb-3">
          <Card.Header>
            <Card.Title as="h6">Identity</Card.Title>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Customer Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="customerName"
                    value={formState.customerName}
                    onChange={handleInputChange}
                    placeholder="e.g. Centene"
                    isInvalid={showValidation && !!errors.customerName}
                  />
                  {showValidation && <Form.Control.Feedback type="invalid">{errors.customerName}</Form.Control.Feedback>}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Program Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="programName"
                    value={formState.programName}
                    onChange={handleInputChange}
                    placeholder="e.g. Centene RX Savings"
                    isInvalid={showValidation && !!errors.programName}
                  />
                  {showValidation && <Form.Control.Feedback type="invalid">{errors.programName}</Form.Control.Feedback>}
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Customer Abbreviation <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="customerNameAbbreviation"
                    value={formState.customerNameAbbreviation}
                    onChange={handleInputChange}
                    placeholder="e.g. CNT"
                    isInvalid={showValidation && !!errors.customerNameAbbreviation}
                  />
                  {showValidation && <Form.Control.Feedback type="invalid">{errors.customerNameAbbreviation}</Form.Control.Feedback>}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Logo URL</Form.Label>
                  <Form.Control
                    type="text"
                    name="logoUrl"
                    value={formState.logoUrl}
                    onChange={handleInputChange}
                    placeholder="e.g. mcs-logo.png"
                  />
                  <p className="field-sub-text">Logo Name in the S3 bucket</p>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header>
            <Card.Title as="h6">Contact</Card.Title>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Support Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="supportNumber"
                    value={formatPhoneNumber(formState.supportNumber)}
                    onChange={handleInputChange}
                    placeholder="e.g. (555) 555-5555"
                    maxLength={14}
                    isInvalid={showValidation && !!errors.supportNumber}
                  />
                  {showValidation && <Form.Control.Feedback type="invalid">{errors.supportNumber}</Form.Control.Feedback>}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Inbound Fax Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="faxNumberInbound"
                    value={formatPhoneNumber(formState.faxNumberInbound)}
                    onChange={handleInputChange}
                    placeholder="e.g. (555) 555-5555"
                    maxLength={14}
                    isInvalid={showValidation && !!errors.faxNumberInbound}
                  />
                  {showValidation && <Form.Control.Feedback type="invalid">{errors.faxNumberInbound}</Form.Control.Feedback>}
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formState.email}
                    onChange={handleInputChange}
                    placeholder="ops@example.com"
                    isInvalid={showValidation && !!errors.email}
                  />
                  {showValidation && <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>}
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header>
            <Card.Title as="h6">Programs</Card.Title>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <ConfigFormSwitch
                  label="Auto Accept"
                  fieldName="isAutoAcceptEnabled"
                  checked={formState.isAutoAcceptEnabled}
                  onChange={handleCheckboxChange}
                  id="auto-accept-switch"
                />
              </Col>
              <Col md={6}>
                <ConfigFormSwitch
                  label="Intro Fax"
                  fieldName="isIntroFaxEnabled"
                  checked={formState.isIntroFaxEnabled}
                  onChange={handleCheckboxChange}
                  id="intro-fax-switch"
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
        <Card className="mb-3">
          <Card.Header>
            <Card.Title as="h6">Automated Calling</Card.Title>
          </Card.Header>
          <Card.Body>
            <Row className="align-items-center">
              <Col md={6}>
                <ConfigFormSwitch
                  label="Bot Calling"
                  fieldName="botCallingAllowed"
                  checked={formState.botCallingAllowed}
                  onChange={handleCheckboxChange}
                  id="bot-calling-allowed-switch"
                />
              </Col>
              {formState.botCallingAllowed && (
                <Col md={6}>
                  <Form.Group className="d-flex align-items-center">
                    <Form.Label htmlFor="bot-entity-name" className="mb-0 mr-3 flex-shrink-0">
                      Name of Bot Caller <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      id="bot-entity-name"
                      className="flex-grow-1"
                      type="text"
                      name="botEntityName"
                      value={formState.botEntityName}
                      onChange={handleInputChange}
                      placeholder="e.g. Acme Bot"
                      isInvalid={showValidation && !!errors.botEntityName}
                    />
                    {showValidation && <Form.Control.Feedback type="invalid">{errors.botEntityName}</Form.Control.Feedback>}
                  </Form.Group>
                </Col>
              )}
            </Row>
          </Card.Body>
        </Card>
        <Card>
          <Card.Header>
            <Card.Title as="h6">Pricing</Card.Title>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <ConfigFormSwitch
                  label="Show Prices"
                  fieldName="showSavingPrices"
                  checked={formState.showSavingPrices}
                  onChange={handleCheckboxChange}
                  id="show-saving-prices-switch"
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Form>
      <div className="d-flex justify-content-end mt-3">
        <Button variant="info" onClick={onCancel} className="mr-2">
          Cancel
        </Button>
        <Button variant="btn btn-primary" onClick={handleSubmit}>
          Create Customer
        </Button>
      </div>
    </>
  );
};

export default CreateCustomerForm;
